import React from 'react';
import { CHART_TOOLTIP_STYLE } from '../../utils/chartColors';
import { formatObservationPeriod } from '../../utils/formatObservationPeriod';

/**
 * Rich tooltip: series values + provenance crumbs for the hovered point.
 *
 * The period crumb must follow the hovered observation (`label` / `payload[].payload.t`),
 * never the series-level `provenance.observationPeriod` (último dato del chart/footer).
 */
export function ChartTooltip({
  active,
  payload,
  label,
  seriesById,
  provenance,
  locale = 'es-AR',
  decimals,
}) {
  if (!active || !payload?.length) return null;

  const lang = String(locale || 'es').startsWith('en') ? 'en' : 'es';
  const rawPeriod =
    label
    ?? payload[0]?.payload?.t
    ?? null;
  const pointPeriod = rawPeriod != null && rawPeriod !== ''
    ? (formatObservationPeriod(rawPeriod, lang) || String(rawPeriod))
    : null;
  // Series-level last period belongs in the chart/widget footer ("Último dato"), not on every hover.
  const showSource = Boolean(provenance?.source);

  return (
    <div className="oikra-chart__tooltip" style={CHART_TOOLTIP_STYLE} role="tooltip">
      {pointPeriod ? <p className="oikra-chart__tooltip-time">{pointPeriod}</p> : null}
      {payload.map((entry) => {
        const meta = seriesById?.[entry.dataKey] || {};
        const value = entry.value;
        const formatted =
          value == null
            ? '—'
            : typeof value === 'number'
              ? value.toLocaleString(locale, decimals == null ? undefined : { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
              : String(value);
        return (
          <div key={entry.dataKey} className="oikra-chart__tooltip-row">
            <span className="oikra-chart__tooltip-label">
              <span
                className="oikra-chart__legend-swatch"
                style={{ color: entry.color || meta.color }}
                aria-hidden
              />
              {meta.label || entry.name || entry.dataKey}
            </span>
            <span>
              {formatted}
              {meta.unit ? ` ${meta.unit}` : ''}
            </span>
          </div>
        );
      })}
      {showSource ? (
        <div className="oikra-chart__tooltip-meta">
          <div>{provenance.source}</div>
        </div>
      ) : null}
    </div>
  );
}
